// What agents can do: the Abilities they are given and the Skills they apply
// (pantallas-abilities-y-skills). One place for the queries the Skills page and
// the agent editor share.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { RagApiError } from '@/services/ApiService'
import { agentsService } from '@/services/agentsService'
import type { SkillPayload } from '@/types/agent'
import { queryKeys } from '../keys'

export function useAbilitiesQuery() {
  return useQuery({ queryKey: queryKeys.abilities.all, queryFn: () => agentsService.listAbilities() })
}

export function useAgentAbilitiesQuery(agentId: string | null) {
  return useQuery({
    queryKey: queryKeys.abilities.ofAgent(agentId ?? ''),
    queryFn: () => agentsService.getAgentAbilities(agentId as string),
    enabled: !!agentId,
  })
}

export function useSkillsQuery() {
  return useQuery({ queryKey: queryKeys.skills.all, queryFn: () => agentsService.listSkills() })
}

function useSkillsChanged() {
  const queryClient = useQueryClient()
  return () => queryClient.invalidateQueries({ queryKey: queryKeys.skills.all })
}

export function useCreateSkill() {
  const changed = useSkillsChanged()
  return useMutation({ mutationFn: (payload: SkillPayload) => agentsService.createSkill(payload), onSuccess: changed })
}

export function useUpdateSkill() {
  const changed = useSkillsChanged()
  return useMutation({
    mutationFn: ({ id, changes }: { id: string; changes: Partial<SkillPayload> }) =>
      agentsService.updateSkill(id, changes),
    onSuccess: changed,
  })
}

export function useDeleteSkill() {
  const changed = useSkillsChanged()
  return useMutation({ mutationFn: (id: string) => agentsService.deleteSkill(id), onSuccess: changed })
}

/** What the server said, not «HTTP 400»: its message travels inside `error`. */
export function serverMessage(error: unknown): string | null {
  if (error instanceof RagApiError) {
    const body = error.body as { error?: { message?: string } } | null | undefined
    return body?.error?.message || null
  }
  return null
}
